import { Content, Label, Tooltip } from '@patternfly/react-core';
import * as React from 'react';

/**
 * Who caused a recorded change, from the `username` on a revision.
 *
 * The value has three distinct meanings and flattening them would mislead: a principal is a
 * person, `system` is a real change with nobody behind it - a scheduler run or a message consumer
 * - and nothing at all means the change predates attribution, including the synthetic baseline
 * revision the audit schema created.
 *
 * Shared by everything that renders `revinfo.username`: the parameter and communication histories,
 * which read it through their own snapshot endpoints, and the entity change log, which reads it
 * through /history. All three are the same column of the same table, so they must read alike.
 *
 * Service accounts arrive here as ordinary principals (`service-account-robot-account`,
 * `client-executive`) and are shown as the name they authenticated with. That is deliberate: the
 * service does not mark them, and guessing from the spelling would mislabel a person one day.
 */
export const ChangeAuthor: React.FunctionComponent<{ username?: string | null }> = ({ username }) => {
  if (!username) {
    return (
      <Tooltip content="Recorded before the service attributed changes to a user.">
        <span style={{ color: 'var(--pf-t--global--text--color--subtle)', fontStyle: 'italic' }}>unattributed</span>
      </Tooltip>
    );
  }

  if (username === 'system') {
    return (
      <Tooltip content="A change with no user behind it, such as the scheduler or a message consumer.">
        <Label isCompact variant="outline">system</Label>
      </Tooltip>
    );
  }

  return <Content component="p">{username}</Content>;
};
